// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import { DateTime } from 'luxon';
import { useEventBus } from 'use-event-bus-hook';
// Store & actions
import useSessionStore from '@/app/store/session';
// Components
import SelectFilter from './select/SelectFilter';
import InputFilter from './input/InputFilter';
import Entity from '../../entity/Entity';
import ICONS from '@/common-utils/enums/icons';
import DateFilter from './date/DateFilter';
import ColumnsManager from '../column/ColumnsManager';
import NumberFilter from './number/NumberFilter';
import PlanFilter from '@/app/modules/sims/components/tables/filters/PlanFilter';
import PopoverHelper from '../../popover/PopoverHelper';
import FilterBadge from './badge/FilterBadge';
import TooltipHelper from '../../tooltip/TooltipHelper';
import SelectInput from '@/common-utils/components/ui/selectInput';
import ModalExport from '../export/ModalExport';
import { Button } from '../../../ui/button';
import { Separator } from '@/common-utils/components/ui/separator';
// Common
import TABLE_CONSTANTS from '../enums/Constants';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import { FILTER_TYPES } from './FilterTypes';
import ModalImport from '../import/ModalImport';
import setTableBodyMaxHeight from '../utils/setTableBodyMaxHeight';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
import SIM_OFFERS_PLANS_OPTIONS from '../../../../../app/modules/sims/enums/SimOffersPlans';
import DATA_TABLE_EVENT_BUS_NAMES from '../enums/DataTableEventBusNames';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';

/**
 *
 * @param props
 * @param props.table
 * @param props.setResetPagination
 * @param props.columns
 * @param props.query
 * @param props.setQuery
 * @param props.setLaunchRefetch
 * @param props.columnOrder
 * @param props.handleColumnOrderChange
 * @param props.selectionMode
 * @param props.pageActions
 * @param props.exportProps
 * @param props.importProps
 * @param props.queryStored
 * @param props.storeQuery
 * @param props.initialQuery
 * @param props.filtersEventName
 * @returns {JSX}
 */
function TableFilters({
    table,
    setResetPagination,
    columns,
    query,
    setQuery,
    queryStored,
    storeQuery,
    setLaunchRefetch,
    columnOrder,
    handleColumnOrderChange,
    selectionMode,
    pageActions = undefined,
    exportProps,
    importProps,
    filtersEventName,
    initialQuery
}) {



    // Store
    const { permissions: userPermissions } = useSessionStore(s => s.session);
    // State
    const [queryFilters, setQueryFilters] = useState(queryStored.filters.length > 0 ? queryStored.filters : query.filters);
    const [columnsManagerOpen, setColumnsManagerOpen] = useState(false);
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
    const [launchSearch, setLaunchSearch] = useState(queryStored.filters.length > 0);
    const [showModalExport, setShowModalExport] = useState(false);
    const [showModalImport, setShowModalImport] = useState(false);
    const [autoFocus, setAutoFocus] = useState(false);

    // Constants
    // Extract hidden filters
    const hiddenFilterKeys = new Set((query.filters ?? []).filter(f => f.hidden).map(f => f.key));

    // Define filters columns
    const columnsDef = columns.map(column => column.filter && ({
        columnId: column.id,
        entity: column.entity,
        namespace: column.namespace,
        filterInfos: column.filter,
        hidden: hiddenFilterKeys.has(column.id)
    })).filter(item => !!item);

    // Define filters options
    const options = columnsDef.filter(col => !col.hidden)
        .map(col => ({
            name: col.entity,
            value: col.columnId,
            namespace: col.namespace
        }));

    const [value, setValue] = useState('');

    // Functions
    const addFilter = (columnId, filterValue) => {
        const filter = queryFilters.find(elem => elem.key === columnId);

        if (filter) {
            filter.value = filterValue;
        } else {
            queryFilters.push({
                key: columnId,
                value: filterValue
            });
        }

        // Makes a copy of queryFilters to force rerender
        const copy = [...queryFilters];
        setQueryFilters(copy);
    };

    const removeFilter = (columnId) => {
        setQueryFilters(queryFilters.filter(filter => filter.key !== columnId));
        setLaunchSearch(true);
    };
    const removeFilters = (filtersToRemove) => {
        let filteredQuery = queryFilters;
        filtersToRemove.forEach(filterToRemove => {
            filteredQuery = filteredQuery.filter(filter => filter.key !== filterToRemove);
        });
        setQueryFilters(filteredQuery);
        setLaunchSearch(true);
    };

    const submitSearch = () => {
        setQuery({
            offset: TABLE_CONSTANTS.OFFSET_STARTING_VALUE,
            limit: TABLE_CONSTANTS.ROWS_NUMBER_PER_PAGE,
            orders: queryStored.orders.length > 0 ? queryStored.orders : query.orders,
            filters: queryFilters
        });
        setLaunchRefetch(true);
        setResetPagination(true);
        table.setPageIndex(0);
        queryStored.filters = queryFilters;
        storeQuery(queryStored);
    };

    const resetSearchQuery = () => {
        storeQuery(initialQuery);
        setQuery(initialQuery);
        setQueryFilters(initialQuery.filters);
        setLaunchRefetch(true);
    };

    const setQueryDefaultFilterValue = (currentFilter) => {
        let queryDefaultFilterValue = queryFilters.filter(filter => filter.key === currentFilter.id);
        if (queryDefaultFilterValue.length !== 0) {
            queryDefaultFilterValue = queryDefaultFilterValue[0].value;
        } else {
            queryDefaultFilterValue = undefined;
        }
        return queryDefaultFilterValue;
    };

    // Hooks
    useEffect(() => {
        setTableBodyMaxHeight();
    }, [queryFilters]);

    useEffect(() => {
        if (launchSearch) {
            setFilterPopoverOpen(false);
            submitSearch();
            setLaunchSearch(false);
        }
    }, [queryFilters, launchSearch, filterPopoverOpen, setFilterPopoverOpen]);

    useEffect(() => {
        if (filterPopoverOpen) {
            setAutoFocus(false);
        } else {
            setAutoFocus(true);
            setValue('');
        }
    }, [filterPopoverOpen]);

    // Event Bus Hooks
    useEventBus(DATA_TABLE_EVENT_BUS_NAMES.OPEN_FILTER, (e) => {
        if (filtersEventName === e.detail.filterEventName) {
            setAutoFocus(false);
            setFilterPopoverOpen(true);
            setValue(e.detail.id);
        }
    });
    useEventBus(DATA_TABLE_EVENT_BUS_NAMES.UPDATE_FILTER, (e) => {
        if (filtersEventName === e.detail.filterEventName) {
            setAutoFocus(false);
            setFilterPopoverOpen(true);
            setValue(e.detail.columnId);
        }
    });

    // Constants
    const tableActions = [
        {
            entity: 'table.actions.export',
            icon: ICONS.DOWNLOAD,
            onClick: () => setShowModalExport(true),
            disabled: !hasRequiredPermissions({
                permissions: userPermissions,
                requiredPermissions: exportProps?.permissions
            }),
            tooltipForbidden: 'title.permissions.access_denied',
            show: !!exportProps
        },
        {
            entity: 'table.actions.import',
            icon: ICONS.UPLOAD,
            onClick: () => setShowModalImport(true),
            disabled: !hasRequiredPermissions({
                permissions: userPermissions,
                requiredPermissions: importProps?.permissions
            }),
            tooltipForbidden: 'title.permissions.access_denied',
            show: !!importProps
        }
    ];

    // Render
    return (
        <div id='table-filters' className='flex flex-col'>
            {/* <Entity entity={'table.filter.title'} /> */}
            <div className='flex h-8 my-2'>
                <PopoverHelper
                    asChild
                    align='center'
                    side='right'
                    isOpen={filterPopoverOpen}
                    onOpenChange={setFilterPopoverOpen}
                    trigger={
                        <Button
                            variant='secondary'
                            icon={ICONS.FILTER}
                            className=' h-8 rounded-lg pr-2 pl-1'
                            entity={'button.filter'}
                        />
                    }
                    contentClass={'flex w-auto p-2'}
                    content={
                        <div className='flex flex-1 items-center sm:flex-row flex-col'>
                            <SelectInput
                                selectId='select-filter-name'
                                options={options}
                                value={value}
                                onChange={setValue}
                                placeholder={<Entity entity={'table.filter.placeholder'} />}
                                autoFocus={autoFocus}
                                className={value && 'sm:mb-0 mb-2'}
                            />
                            {
                                value &&
                                <span className='sm:block hidden sm:px-2'>:</span>
                            }
                            <div className='flex'>
                                {
                                    columns.filter(column => column.id === value && !hiddenFilterKeys.has(column.id)).map((column) => {
                                        switch (column.filter.type) {
                                            case FILTER_TYPES.INPUT: {
                                                return <InputFilter
                                                    filtersEventName={filtersEventName}
                                                    addFilter={addFilter}
                                                    removeFilter={removeFilter}
                                                    columnId={column.id}
                                                    defaultValue={queryFilters.find(filter => filter.key === column.id)?.value}
                                                    resetThisFilter={!queryFilters.some(filter => filter.key === column.id)}
                                                    entity={column.filter.placeholder}
                                                    namespace={column.namespace}
                                                    setLaunchSearch={setLaunchSearch}
                                                    key={column.id}
                                                />;
                                            }
                                            case FILTER_TYPES.SELECT: {
                                                return <SelectFilter
                                                    values={column.filter.values}
                                                    addFilter={addFilter}
                                                    removeFilter={removeFilter}
                                                    columnId={column.id}
                                                    placeholder={column.filter.placeholder ?? <Entity entity={'table.filter.filter_by'} />}
                                                    resetThisFilter={!queryFilters.some(filter => filter.key === column.id)}
                                                    namespace={column.namespace}
                                                    defaultValue={setQueryDefaultFilterValue(column)}
                                                    setLaunchSearch={setLaunchSearch}
                                                    sortOptions={column.filter.sortOptions}
                                                    key={column.id}
                                                />;
                                            }
                                            case FILTER_TYPES.DATE: {
                                                return <DateFilter
                                                    addFilter={addFilter}
                                                    removeFilter={removeFilter}
                                                    columnId={column.id}
                                                    resetThisFilter={!queryFilters.some(filter => filter.key === column.id)}
                                                    key={column.id}
                                                    setLaunchSearch={setLaunchSearch}
                                                    isLocalDateAndTime={column.filter.isLocalDateAndTime}
                                                />;
                                            }
                                            case FILTER_TYPES.NUMBER: {
                                                return <NumberFilter
                                                    addFilter={addFilter}
                                                    removeFilter={removeFilter}
                                                    columnId={column.id}
                                                    filterByText={column.id.includes('Amount') ? 'amount' : 'number'}
                                                    resetThisFilter={!queryFilters.some(filter => filter.key === column.id)}
                                                    setLaunchSearch={setLaunchSearch}
                                                    key={column.id}
                                                />;
                                            }
                                            case FILTER_TYPES.PLAN: {
                                                return <PlanFilter
                                                    addFilter={addFilter}
                                                    removeFilter={removeFilters}
                                                    columnId={column.id}
                                                    resetThisFilter={!queryFilters.some(filter => filter.key === column.id)}
                                                    key={column.id}
                                                    setLaunchSearch={setLaunchSearch}
                                                />;
                                            }
                                        }
                                    })
                                }
                            </div>
                        </div>


                    }
                />

                <div className='flex flex-1 justify-end' >
                    {
                        pageActions &&
                        <>
                            {/* <Separator orientation='vertical' className='h-full mx-2' /> */}
                            {pageActions}
                        </>
                    }
                </div>

                {
                    !selectionMode &&
                    <div className='flex gap-2'>
                        {pageActions && <Separator orientation='vertical' className='h-full ml-2' />}
                        {tableActions.map((action) =>
                            action.show && (
                                <TooltipHelper
                                    key={action.entity}
                                    asChild
                                    trigger={
                                        <Button
                                            variant='secondary'
                                            icon={action.icon}
                                            onClick={action.onClick}
                                            className=' h-8 rounded-lg'
                                            disabled={action.disabled}
                                            dataCy={action.entity}
                                        />
                                    }
                                    content={<Entity entity={action.entity} />}
                                />
                            ))
                        }
                        <PopoverHelper
                            asChild
                            isOpen={columnsManagerOpen}
                            onOpenChange={setColumnsManagerOpen}
                            trigger={
                                <Button
                                    variant='secondary'
                                    icon={ICONS.COLUMNS}
                                    className=' h-8 rounded-lg'
                                />
                            }
                            content={
                                <ColumnsManager
                                    columns={table.getAllColumns()}
                                    columnsToDisplay={columnOrder}
                                    handleColumnOrderChange={handleColumnOrderChange}
                                    setColumnsManagerOpen={setColumnsManagerOpen}
                                />
                            }
                            contentClass='max-h-[70vh] overflow-y-auto'
                        />
                    </div>
                }
            </div>
            <div className={queryFilters.filter(elem => !elem.hidden).length > 0 ? 'flex flex-wrap gap-2 my-2' : ''}>
                {
                    queryFilters.filter(elem => !elem.hidden).length > 0 &&
                    <TooltipHelper
                        asChild
                        trigger={
                            <Button
                                onClick={() => {
                                    resetSearchQuery();
                                }}
                                icon={ICONS.DELETE}
                                //entity={'button.reset_filters'}
                                className={'rounded-lg h-8'}
                                variant={'ghost'}
                            />
                        }
                        content={<Entity entity={'button.reset_filters'} />}
                    />
                }
                {
                    queryFilters.filter(elem => !elem.hidden).map((filter) => {
                        const displayedColumn = {};
                        const displayedValue = {};
                        const filterAndColumnsInfos = columnsDef.find(x => x.columnId === filter.key);

                        if (filterAndColumnsInfos) {
                            displayedColumn.entity = filterAndColumnsInfos.entity;
                            displayedColumn.namespace = filterAndColumnsInfos.namespace;

                            if (filterAndColumnsInfos.filterInfos.type === FILTER_TYPES.INPUT) {
                                displayedValue.filterValue = filter.value;
                            }
                            if (filterAndColumnsInfos.filterInfos.type === FILTER_TYPES.SELECT) {
                                const filterInfos = Array.isArray(filterAndColumnsInfos.filterInfos.values)
                                    ? filterAndColumnsInfos.filterInfos.values.find(item => item.name === filter.value)
                                    : filterAndColumnsInfos.filterInfos.values[filter.value];
                                displayedValue.value = filter.value;
                                displayedValue.filterValue = filterInfos?.entity;
                                displayedValue.namespace = filterInfos?.namespace || filterAndColumnsInfos?.namespace;
                            }
                            if (filterAndColumnsInfos.filterInfos.type === FILTER_TYPES.DATE) {
                                if (filter.value.beforeSelected) {
                                    displayedValue.filterValue =
                                        `${getTranslatedText('date_picker.before_date')} ${DateTime.fromISO(filter.value.endDateSelected).toFormat('D')}`;
                                } else if (filter.value.afterSelected) {
                                    displayedValue.filterValue =
                                        `${getTranslatedText('date_picker.after_date')} ${DateTime.fromISO(filter.value.startDateSelected).toFormat('D')}`;
                                } else {
                                    displayedValue.filterValue =
                                        // eslint-disable-next-line max-len
                                        `${DateTime.fromISO(filter.value.startDateSelected).toFormat('D')} - ${DateTime.fromISO(filter.value.endDateSelected).toFormat('D')}`;
                                }
                            }
                            if (filterAndColumnsInfos.filterInfos.type === FILTER_TYPES.NUMBER) {
                                if (filter.value.includes('__value__')) {
                                    displayedValue.entity = 'number_filter.badge.range';
                                    displayedValue.filterValue = filter.value.split('__value__');
                                } else if (filter.value.includes('__value')) {
                                    displayedValue.entity = 'number_filter.badge.more';
                                    displayedValue.filterValue = filter.value.split('__value')[0];
                                } else if (filter.value.includes('value__')) {
                                    displayedValue.entity = 'number_filter.badge.less';
                                    displayedValue.filterValue = filter.value.split('value__')[1];
                                } else {
                                    displayedValue.entity = 'number_filter.badge.equal';
                                    displayedValue.filterValue = filter.value;
                                }
                            }
                            if (filterAndColumnsInfos.filterInfos.type === FILTER_TYPES.PLAN) {
                                // Sim Card Specific Filter
                                // 3 filters in 1 : sim.tariffData, sim.tariffDataUnit, sim.tariffName
                                let planFilters = queryFilters.filter(elem => elem.key.includes('sim.tariff'));
                                // display the 'ALL' plan filter name not applied in queryFilters
                                planFilters = [
                                    ...planFilters,
                                    {
                                        key: 'sim.tariffName',
                                        value: planFilters.find(elem => elem.key === 'sim.tariffName')?.value ?? SIM_OFFERS_PLANS_OPTIONS.ALL.value
                                    }
                                ];
                                displayedColumn.entity = 'filter.badge.label';
                                displayedColumn.namespace = nsSimCard;
                                displayedColumn.keys = planFilters.map(fltr => fltr.key);
                                displayedValue.entity = 'filter.badge.value';
                                displayedValue.filterValue = planFilters.map(fltr => fltr.value);
                                displayedValue.namespace = nsSimCard;
                            }

                            return (
                                <FilterBadge
                                    key={filter.key}
                                    displayedValue={displayedValue}
                                    removeFilter={removeFilter}
                                    removeFilters={removeFilters}
                                    filterKey={displayedColumn.keys ?? filter.key}
                                    entity={displayedColumn?.entity}
                                    namespace={displayedColumn?.namespace}
                                    filtersEventName={filtersEventName}
                                />
                            );
                        }
                    })
                }
            </div>
            {
                showModalExport &&
                <ModalExport
                    isOpen={showModalExport}
                    setIsOpen={setShowModalExport}
                    exportApi={exportProps?.api}
                    uuidToExport={exportProps?.uuid}
                    queryFilters={queryFilters}
                    customExportQuery={exportProps?.query}
                />
            }
            {
                showModalImport &&
                <ModalImport
                    isOpen={showModalImport}
                    setIsOpen={setShowModalImport}
                    importProps={importProps}
                />
            }
        </div >
    );
}
TableFilters.propTypes = {
    table: PropTypes.object.isRequired,
    setResetPagination: PropTypes.func.isRequired,
    columns: PropTypes.array.isRequired,
    query: PropTypes.object.isRequired,
    setQuery: PropTypes.func.isRequired,
    queryStored: PropTypes.object.isRequired,
    storeQuery: PropTypes.func.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired,
    columnOrder: PropTypes.array,
    handleColumnOrderChange: PropTypes.func,
    selectionMode: PropTypes.bool,
    pageActions: PropTypes.node,
    exportProps: PropTypes.shape({
        permissions: PropTypes.array,
        api: PropTypes.object,
        uuid: PropTypes.string,
        query: PropTypes.object
    }),
    importProps: PropTypes.object,
    filtersEventName: PropTypes.string,
    initialQuery: PropTypes.object
};
export default TableFilters;